import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  HeartOutlined,
  HeartFilled,
} from '@ant-design/icons';
import { AppBarCMS } from '../../../../basic/index.jsx';
import { CollapseBlock, Spinner } from '../../../../common/index.jsx';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import { useStudyProgramsDataGridFilter } from '../../../../common/DataGrid/filters/DataGridFilters.jsx';
import TableFilter from '../../../../common/TableFilter/TableFilter.jsx';
import {
  useGetIngProjectStudentsQuery,
  useUpdateApplicationTeacherPriorityMutation,
} from '../../../../../redux/services/ing_projects.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';

const IngProjectsStudentsPage = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const VIEW_MODE_STORAGE_KEY = 'cms-ing-projects-project-students-view-mode';
  const [viewMode, setViewMode] = useState('assigned');

  const { data, isLoading, refetch } = useGetIngProjectStudentsQuery(projectId, {
    refetchOnMountOrArgChange: true,
  });
  const [updateTeacherPriority] = useUpdateApplicationTeacherPriorityMutation();
  const { successMessage, errorMessage } = useToastMessages();
  const project = data?.project;
  const applications = data?.applications || [];
  const assignedStudents = data?.assigned_students || [];
  const canManageTeacherPriority = !!project?.is_guarantor;
  const isApplicationsView = viewMode === 'applications';
  const availableViewModes = useMemo(
    () => [
      { value: 'assigned', label: t('Priradení študenti') },
      ...(!project?.archived_at
        ? [{ value: 'applications', label: t('Záujemcovia o projekt') }]
        : []),
    ],
    [project?.archived_at, t],
  );

  // Filter props for text columns
  const firstNameFilterProps = useDataGridTextFilter(['student', 'person', 'first_name']);
  const lastNameFilterProps = useDataGridTextFilter(['student', 'person', 'last_name']);
  const yearFilterProps = useDataGridTextFilter('year');
  const applicationsStudyProgramFilterProps = useStudyProgramsDataGridFilter(
    'study_programs',
    applications.map((record) => ({
      ...record,
      study_programs: record.study_program ? [record.study_program] : [],
    })),
  );
  const assignedStudentsStudyProgramFilterProps = useStudyProgramsDataGridFilter(
    'study_programs',
    assignedStudents.map((record) => ({
      ...record,
      study_programs: record.study_program ? [record.study_program] : [],
    })),
  );

  useEffect(() => {
    document.title = t('Študenti projektu') + ' | FRI UNIZA';
  }, [t]);

  useEffect(() => {
    const storedViewMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY);

    if (['assigned', 'applications'].includes(storedViewMode)) {
      setViewMode(storedViewMode);
    }
  }, []);

  useEffect(() => {
    if (
      !availableViewModes.some((availableViewMode) => availableViewMode.value === viewMode)
    ) {
      setViewMode('assigned');
    }
  }, [availableViewModes, viewMode]);

  const handleTeacherPriorityChange = useCallback(
    async (applicationId, checked) => {
      try {
        await updateTeacherPriority({
          applicationId,
          teacherPriority: checked ? 'Áno' : 'Nie',
        }).unwrap();
        successMessage({ message: t('Priorita učiteľa bola aktualizovaná') });
        refetch();
      } catch {
        errorMessage({ message: t('Nepodarilo sa aktualizovať prioritu učiteľa') });
      }
    },
    [updateTeacherPriority, t, successMessage, errorMessage, refetch],
  );

  const createColumns = useCallback(
    (studyProgramFilterProps, yearTitle, defaultYearSortOrder) => [
      {
        title: t('Priezvisko'),
        dataIndex: ['student', 'person', 'last_name'],
        key: 'last_name',
        ...lastNameFilterProps,
      },
      {
        title: t('Meno'),
        dataIndex: ['student', 'person', 'first_name'],
        key: 'first_name',
        ...firstNameFilterProps,
      },
      {
        title: t('Št. program'),
        dataIndex: ['study_program', 'shortcut'],
        key: 'study_program',
        render: (text) => text || '–',
        ...studyProgramFilterProps,
      },
      {
        title: t('Priorita študenta'),
        key: 'priority',
        render: (_, record) => record.priority ?? record.application?.priority ?? '–',
        sorter: (a, b) =>
          Number(a.priority ?? a.application?.priority ?? 0) -
          Number(b.priority ?? b.application?.priority ?? 0),
        filters: [
          { text: '1', value: 1 },
          { text: '2', value: 2 },
          { text: '3', value: 3 },
          { text: '4', value: 4 },
          { text: '5', value: 5 },
          { text: '6', value: 6 },
        ],
        onFilter: (value, record) =>
          Number(record.priority ?? record.application?.priority) === Number(value),
      },
      {
        title: t('Priorita učiteľa'),
        key: 'teacher_priority',
        render: (_, record) => {
          const value = record.teacher_priority ?? record.application?.teacher_priority;

          return value === 'Áno' ? t('Áno') : value === 'Nie' ? t('Nie') : '–';
        },
        sorter: (a, b) =>
          `${a.teacher_priority ?? a.application?.teacher_priority ?? ''}`.localeCompare(
            `${b.teacher_priority ?? b.application?.teacher_priority ?? ''}`,
          ),
        filters: [
          { text: t('Áno'), value: 'Áno' },
          { text: t('Nie'), value: 'Nie' },
        ],
        onFilter: (value, record) =>
          (record.teacher_priority ?? record.application?.teacher_priority ?? '') === value,
      },
      {
        title: yearTitle,
        dataIndex: 'year',
        key: 'year',
        defaultSortOrder: defaultYearSortOrder,
        sorter: (a, b) => (a.year || 0) - (b.year || 0),
        ...yearFilterProps,
      },
    ],
    [t, firstNameFilterProps, lastNameFilterProps, yearFilterProps],
  );

  const applicationsColumns = useMemo(
    () => createColumns(applicationsStudyProgramFilterProps, t('Rok žiadosti'), 'ascend'),
    [createColumns, applicationsStudyProgramFilterProps, t],
  );

  const assignedStudentsColumns = useMemo(
    () => createColumns(assignedStudentsStudyProgramFilterProps, t('Rok nástupu'), 'descend'),
    [createColumns, assignedStudentsStudyProgramFilterProps, t],
  );

  const columns = isApplicationsView ? applicationsColumns : assignedStudentsColumns;
  const students = isApplicationsView ? applications : assignedStudents;

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={`${t('Študenti projektu')}: ${project?.name || ''}`}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
        mainActions={[]}
      />

      <div style={{ margin: '10px' }}>
        <TableFilter
          title={t('Filter študentov')}
          storageKey={VIEW_MODE_STORAGE_KEY}
          value={viewMode}
          setValue={setViewMode}
          options={availableViewModes}
        />
        <CollapseBlock>
          <DataGrid
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={students}
            customSettings={isApplicationsView && canManageTeacherPriority
              ? (record) => (
                  <IconButton
                    icon={record.teacher_priority === 'Áno' ? <HeartFilled /> : <HeartOutlined />}
                    tooltip={
                      record.is_assigned_to_project
                        ? t('Študent je už priradený na projekt, prioritu nie je možné meniť')
                        : record.teacher_priority === 'Áno'
                          ? t('Odstrániť prioritu')
                          : t('Pridať prioritu')
                    }
                    onClick={() => !record.is_assigned_to_project && handleTeacherPriorityChange(record.id, record.teacher_priority !== 'Áno')}
                    disabled={record.is_assigned_to_project}
                  />
                )
              : undefined}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default IngProjectsStudentsPage;
